import React, { useEffect, useMemo, useState } from "react";
import {
  ExpenseCategory,
  listExpenseCategories,
  createExpenseCategory,
  updateBusinessExpense,
} from "../../services/financeApi";

type BusinessExpense = {
  _id: string;
  description: string;
  cost: number;
  createdAt: string;
  date?: string;
  expenseDate?: string;
  category?: string;
};

const toInputDate = (value?: string) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

const getExpenseDate = (expense: BusinessExpense | null) =>
  expense?.date || expense?.expenseDate || expense?.createdAt || "";

export default function EditExpenseModal({
  open,
  onClose,
  expense,
  year,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  expense: BusinessExpense | null;
  year: string;
  onSaved?: () => void;
}) {
  const [desc, setDesc] = useState("");
  const [cost, setCost] = useState<string>("");
  const [date, setDate] = useState("");
  const [category, setCategory] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const [cats, setCats] = useState<ExpenseCategory[]>([]);
  const [addingNew, setAddingNew] = useState(false);
  const [newCat, setNewCat] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const c = await listExpenseCategories();
        setCats(c);
      } catch {
        setCats([]);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (open && expense) {
      setDesc(expense.description || "");
      setCost(String(expense.cost ?? ""));
      setDate(toInputDate(getExpenseDate(expense)));
      setCategory(expense.category || "");
    }
  }, [open, expense]);

  const canSave = useMemo(
    () =>
      desc.trim().length > 0 &&
      !isNaN(Number(cost)) &&
      Number(cost) > 0 &&
      /^\d{4}-\d{2}-\d{2}$/.test(date),
    [desc, cost, date]
  );

  const ensureCategory = async () => {
    const name = (category || "").trim();
    if (!name) return;
    const exists = cats.some(
      (c) => c.name.toLowerCase() === name.toLowerCase()
    );
    if (!exists) {
      try {
        const created = await createExpenseCategory(name);
        setCats((prev) => [...prev, created]);
      } catch {}
    }
  };

  const save = async () => {
    if (!expense?._id || !canSave) return;
    setLoading(true);
    try {
      await ensureCategory();
      await updateBusinessExpense(year, expense._id, {
        description: desc.trim(),
        cost: Number(cost),
        createdAt: date,
        category: category.trim() || undefined,
      });
      onSaved?.();
      onClose();
    } catch (e) {
      // noop UI; you can toast here if you have a global toaster
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute left-1/2 top-16 -translate-x-1/2 w-[92vw] max-w-md rounded-md border bg-white shadow">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="font-medium">Edit Expense</div>
          <button onClick={onClose} className="text-slate-500">
            ✕
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs text-slate-600 mb-1">
              Description
            </label>
            <input
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder="e.g., Booth fee"
            />
          </div>

          <div>
            <label className="block text-xs text-slate-600 mb-1">
              Category
            </label>

            {!addingNew ? (
              <div className="flex gap-2">
                <select
                  className="flex-1 border rounded px-2 py-1 text-sm bg-white"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">— None —</option>
                  {cats.map((c) => (
                    <option key={c._id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <button
                  className="px-2 py-1 text-xs border rounded hover:bg-slate-50"
                  onClick={() => setAddingNew(true)}
                >
                  Add new
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  value={newCat}
                  onChange={(e) => setNewCat(e.target.value)}
                  placeholder="New category"
                  className="flex-1 border rounded px-2 py-1 text-sm"
                />
                <button
                  className="px-2 py-1 text-xs border rounded bg-blue-600 text-white disabled:opacity-50"
                  disabled={!newCat.trim()}
                  onClick={() => {
                    setCategory(newCat.trim());
                    setNewCat("");
                    setAddingNew(false);
                  }}
                >
                  Use
                </button>
                <button
                  className="px-2 py-1 text-xs border rounded"
                  onClick={() => {
                    setNewCat("");
                    setAddingNew(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs text-slate-600 mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-slate-600 mb-1">Amount</label>
            <input
              value={cost}
              onChange={(e) =>
                setCost(
                  e.target.value
                    .replace(/[^0-9.]/g, "")
                    .replace(/(\..*)\./, "$1")
                )
              }
              className="w-full border rounded px-2 py-1 text-sm"
              inputMode="decimal"
              placeholder="0.00"
            />
          </div>

          {getExpenseDate(expense) && (
            <div className="text-[11px] text-slate-500">
              Expense date{" "}
              {new Date(getExpenseDate(expense)).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <button
            className="px-3 py-1.5 text-sm border rounded"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white disabled:opacity-50"
            disabled={!canSave || loading}
            onClick={save}
          >
            {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
